import {
  CheckoutDetailMainWrapper,
  DescriptionCell,
  LabelCell,
  ProductDetailWrapper,
  ProductOptionWrapper,
} from "../../../shared/styles";
import {
  AuthService,
  CustomLabel,
  FlexContainer,
  InfoIcon,
  Input,
  nameof,
  SingleSelect,
  withRouter,
} from "@mindmemobile/mm-shared-global";
import { observer } from "mobx-react";
import React, { FC, ReactNode, useEffect } from "react";
import { NavigateFunction } from "react-router-dom";
import { CheckOutStore } from "../../../shared/store";
import {
  MerchantAccountService,
  SellerService,
} from "../../../shared/services";
import {
  CheckoutDetailPageModel,
  displayCardData,
  MerchantAccount,
  SellerModel,
} from "../../../shared/models";
import { ApplicationStatus, CheckOutDisplayTypes } from "../../../shared/enums";
import {
  ItemHeader,
  MerchantAccountsDropDown,
} from "../../../shared/components";

type IProps = {
  checkoutStore: CheckOutStore;
  navigate: NavigateFunction;
};

/**
 * The checkout edit API returns the selected merchant account without its
 * payment settings (CheckoutDetailPageModel.deserializeAPIModel only maps
 * ids/name/gateway), so an account loaded that way reports cards.isEnabled
 * false and a "0.00" surcharge percentage. Copy the real settings across from
 * the fetched account list, keeping every other field on the saved account
 * (notably collectJSKey, which the list does not carry) untouched.
 */
function withResolvedPaymentSettings(
  selected: MerchantAccount,
  accounts: MerchantAccount[],
): MerchantAccount {
  const matched = accounts.find(
    (account) =>
      (!!selected.merchantAccountId &&
        account.merchantAccountId === selected.merchantAccountId) ||
      (!!selected.id && account.id === selected.id),
  );
  if (!matched) return selected;
  return new MerchantAccount({
    ...selected,
    paymentSettings: matched.paymentSettings,
  });
}

const DetailPage: FC<IProps> = (props: IProps) => {
  useEffect(() => {
    getMerchantAccounts();
    getSellers();
  }, []);

  const getMerchantAccounts = () => {
    const { selectedMerchantAccount } = props.checkoutStore.detailPage;
    MerchantAccountService.getMerchantAccounts(
      AuthService.authData.accountId,
      1,
      10000,
      "",
    ).subscribe((response) => {
      if (response.result) {
        const activeMerchantAccounts = (
          response.result?.map((m) => MerchantAccount.deserializeAPIModel(m)) ||
          []
        ).filter((f) => f.applicationStatus === ApplicationStatus.Active);

        props.checkoutStore.updateMerchantAccounts(activeMerchantAccounts);
        const resolvedMerchantAccount = selectedMerchantAccount.id
          ? withResolvedPaymentSettings(
              selectedMerchantAccount,
              activeMerchantAccounts,
            )
          : activeMerchantAccounts[0];
        props.checkoutStore.updateDetailPage(
          new CheckoutDetailPageModel({
            ...props.checkoutStore.detailPage,
            selectedMerchantAccount: resolvedMerchantAccount,
          }),
        );

        /* A checkout with no saved merchant account is being created, so the
           account defaulted to above is a fresh selection and its surcharge
           defaults apply. A saved checkout keeps whatever it stored, except
           for a missing rate, which falls back to the selected account's. */
        if (!selectedMerchantAccount.id) {
          props.checkoutStore.applyMerchantSurchargeDefaults(
            activeMerchantAccounts[0],
          );
        } else {
          props.checkoutStore.applyMerchantSurchargePercentageFallback();
        }
      }
    });
  };
  const getSellers = () => {
    const { selectedSeller } = props.checkoutStore.detailPage;
    SellerService.getSellerNames(AuthService.authData.accountId).subscribe(
      (response) => {
        if (response.result) {
          const activeSellers = (
            response.result?.map((m) => SellerModel.deserializeAPIModel(m)) ||
            []
          ).filter((f) => f.isActive === true);

          props.checkoutStore.updateSellers(activeSellers);
          props.checkoutStore.updateDetailPage(
            new CheckoutDetailPageModel({
              ...props.checkoutStore.detailPage,
              selectedSeller:
                selectedSeller?.id !== "" ? selectedSeller : activeSellers[0],
            }),
          );
        }
      },
    );
  };

  const onChange = (
    value: string | number | boolean,
    key: keyof CheckoutDetailPageModel,
  ) => {
    const clonedObject = new CheckoutDetailPageModel({
      ...props.checkoutStore.detailPage,
    });

    switch (key) {
      case "name":
        clonedObject.name = value.toString();
        break;
      case "isActive":
        clonedObject.isActive = value as boolean;
        break;
      case "selectedDisplayType":
        clonedObject.selectedDisplayType =
          value as keyof typeof CheckOutDisplayTypes;
        break;
      default:
        break;
    }
    props.checkoutStore.updateDetailPage(
      new CheckoutDetailPageModel(clonedObject),
    );
  };

  const input = (
    type: "text",
    value: any,
    fieldName: keyof CheckoutDetailPageModel,
    placeHolder: string,
    className?: string,
  ) => {
    return (
      <Input
        value={value}
        error={props.checkoutStore.detailPage.hasError(
          nameof<CheckoutDetailPageModel>(fieldName),
        )}
        errorMessage={""}
        id={`txt_${fieldName}`}
        type={type}
        placeholder={placeHolder}
        onChange={(e) => onChange(e.target.value, fieldName)}
        className={className}
      />
    );
  };

  function onChangeDisplayType(
    displayType: keyof typeof CheckOutDisplayTypes,
    isDoantionOnly: boolean,
  ) {
    const clonedObject = new CheckoutDetailPageModel({
      ...props.checkoutStore.detailPage,
    });
    clonedObject.selectedDisplayType = displayType;
    clonedObject.showSingleItemPrice = displayType === "SingleItem";
    clonedObject.isDonationOnly = isDoantionOnly;
    props.checkoutStore.updateDetailPage(
      new CheckoutDetailPageModel(clonedObject),
    );
  }

  const renderDisplayCards = (): ReactNode => {
    const { selectedDisplayType: selectedItemType, isDonationOnly } =
      props.checkoutStore.detailPage;
    const cards = displayCardData.single.concat(displayCardData.multiple);
    return (
      <FlexContainer spacing={45} className="layout-outer-width">
        {cards.map((card, index) => {
          const isActive =
            selectedItemType === card.id &&
            (card.isDonationOnly === true) === isDonationOnly;
          const isError = props.checkoutStore.detailPage.hasError(
            nameof<CheckoutDetailPageModel>("selectedDisplayType"),
          );

          return (
            <div key={index}>
              <div
                className={` ${isActive ? "active" : ""} single-checkout-card ${isError ? "error" : ""}`}
              >
                <img
                  src={isActive ? card.activeImage : card.image}
                  alt="Display"
                  onClick={() =>
                    onChangeDisplayType(card.id, card.isDonationOnly === true)
                  }
                />
              </div>
              <FlexContainer fxLayout="column" spacing={2} alignItems="center">
                <CustomLabel
                  lineHeight="18px"
                  fontWeight={600}
                  fontSize={"14px"}
                  margin="0px 0px 8px 0px"
                >
                  {card.label1}
                </CustomLabel>
                <CustomLabel lineHeight="14px" fontSize={"11px"}>
                  {card.label2}
                </CustomLabel>
                <CustomLabel lineHeight="14px" fontSize={"11px"}>
                  {" "}
                  {card.label3}
                </CustomLabel>
              </FlexContainer>
            </div>
          );
        })}
      </FlexContainer>
    );
  };

  const renderDisplaySection = () => {
    return (
      <FlexContainer fxLayout="column" spacing={30}>
        <div>{renderDisplayCards()}</div>
      </FlexContainer>
    );
  };

  const renderMerchantAccountSection = () => {
    const { selectedMerchantAccount } = props.checkoutStore.detailPage;
    const { merchantAccounts } = props.checkoutStore;

    const handleItemClick = (item: MerchantAccount) => {
      props.checkoutStore.updateDetailPage(
        new CheckoutDetailPageModel({
          ...props.checkoutStore.detailPage,
          selectedMerchantAccount: item,
        }),
      );
      /* Picking an account pulls in its Credit Card surcharge defaults. The
         checkout owns the copy from here on. */
      props.checkoutStore.applyMerchantSurchargeDefaults(item);
    };

    return (
      <>
        <MerchantAccountsDropDown
          selectedMerchantAccount={selectedMerchantAccount}
          merchantAccounts={merchantAccounts}
          showError={props.checkoutStore.detailPage.hasError(
            nameof<CheckoutDetailPageModel>("selectedMerchantAccount"),
          )}
          onChange={(option) => handleItemClick(option)}
          className="white-bg"
        />
      </>
    );
  };

  const renderSoldBySection = () => {
    const { selectedSeller } = props.checkoutStore.detailPage;
    const { sellers } = props.checkoutStore;
    const handleItemClick = (item: SellerModel) => {
      props.checkoutStore.updateDetailPage(
        new CheckoutDetailPageModel({
          ...props.checkoutStore.detailPage,
          selectedSeller: item,
        }),
      );
    };
    return (
      <>
        <SingleSelect
          selectedKey={selectedSeller.name}
          nameKeys={["name"]}
          valueKeys={["id"]}
          options={[sellers]}
          customHeader={
            selectedSeller.name ? (
              <FlexContainer spacing={10} alignItems="center">
                {/* {selectedSeller.logoURL && (
                  <img src={selectedSeller.logoURL} className="logo-in-dpdn" />
                )} */}
                {selectedSeller.name}
              </FlexContainer>
            ) : (
              "Select Seller"
            )
          }
          customOption={(option: SellerModel) => (
            <FlexContainer
              padding="0px 0px 5px"
              key={option.id}
              spacing={10}
              alignItems="center"
            >
              {/* {option.logoURL && (
                <img src={option.logoURL} className="logo-in-dpdn" />
              )} */}
              <span>{option.name}</span>
            </FlexContainer>
          )}
          emptySelection="Select Seller"
          onChange={(option) => handleItemClick(option)}
          className={"dpdn-with-icons seller-search-dpdn"}
          /* Accounts can have a long seller list, so let the name be typed
             rather than scrolled for. Filtering falls back to nameKeys. */
          searchable
          searchPlaceholder="Search sellers"
          hasError={props.checkoutStore.detailPage.hasError(
            nameof<CheckoutDetailPageModel>("selectedSeller"),
          )}
          disabled={props.checkoutStore.sellers.length <= 1}
        />
      </>
    );
  };

  return (
    <CheckoutDetailMainWrapper>
      <ProductDetailWrapper>
        <ItemHeader header="Details" />
        <ProductOptionWrapper>
          <FlexContainer className="label-description-row label-desc-row-60">
            <LabelCell>
              <CustomLabel>Name</CustomLabel>
            </LabelCell>
            <div>
              <FlexContainer spacing={15} alignItems="center">
                {input(
                  "text",
                  props.checkoutStore.detailPage.name,
                  "name",
                  "Name",
                  "styled-input prod-name-input",
                )}
                {/* <CustomLabel lineHeight="40px" fontSize="16px" margin="0px 0px 0px 50px">
                                  Status
                              </CustomLabel>
                              <DescriptionCell>
                                  <ToggleSwitch
                                      on={props.checkoutStore.detailPage.isActive || false}
                                      onChange={(value) => onChange(value, 'isActive')}
                                  />
                              </DescriptionCell> */}
              </FlexContainer>
            </div>
          </FlexContainer>
          {/* <FlexContainer className="label-description-row">
                      <LabelCell>
                          <CustomLabel>Type</CustomLabel>
                      </LabelCell>
                      <div>{renderTypeSection()}</div>
                  </FlexContainer> */}
          <FlexContainer className="label-description-row label-desc-row-60 layout-outer-wrapper">
            <LabelCell>
              <CustomLabel>Layout</CustomLabel>
              <InfoIcon />
            </LabelCell>
            <div className="flex-auto-grow">{renderDisplaySection()}</div>
          </FlexContainer>
          <FlexContainer className="label-description-row label-desc-row-60">
            <LabelCell>
              <CustomLabel>Merchant Account</CustomLabel>
            </LabelCell>
            <DescriptionCell>{renderMerchantAccountSection()}</DescriptionCell>
          </FlexContainer>
          <FlexContainer className="label-description-row label-desc-row-60">
            <LabelCell>
              <CustomLabel>Sold By</CustomLabel>
            </LabelCell>
            <DescriptionCell className="detail-page-soldby-dpdn">
              {renderSoldBySection()}
            </DescriptionCell>
          </FlexContainer>
        </ProductOptionWrapper>
      </ProductDetailWrapper>
    </CheckoutDetailMainWrapper>
  );
};

export default withRouter(observer(DetailPage));
